// @flow
import * as React from 'react';
import { forwardRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton, MenuItem,
  Stack
} from '@mui/material';
import { useClient } from '../../hooks/client-context';
import { LoadingButton } from '@mui/lab';
import { Pen, Plus } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  SaveUserProfileMutation,
  useCheckUserExistenceByEmailLazyQuery, useMergeClientsInOneGroupMutation,
  useSaveStatusMutation, ZipSelector
} from '../../../../shared';
import {
  formatToPhoneNumber,
  FormikDateInput,
  FormikSelectInput,
  FormikTextInput,
  PhoneNumberInput
} from '@coverright/ui/inputs';
import { ModalCloseButton } from '@coverright/ui/shared';
import moment from 'moment';
import { useSaveProfile } from './hooks/use-save-profile';
import {
  useCreateMedicareQuoteForClientMutation
} from '@coverright/data-access/medicare';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { GetDefaultPlansYear } from '@coverright/utils';
import { useNavigate } from 'react-router-dom';
import { getRoute } from '../../../../app/router';
import { keycloak } from '@coverright/shared/keycloak';
import { MedicareQuoteFilterExtraBenefit } from '@coverright/data-access/types/medicare';
import { StatusDto } from '@coverright/data-access/types/enrollment';

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Enter a valid email'),
  firstName: yup
    .string()
    .required('First name is required'),
  lastName: yup
    .string()
    .required('Last name is required'),
  gender: yup
    .string()
    .required('Gender is required'),
  phoneNumber: yup
    .string()
    .required('Phone is required')
  ,
  phoneNumber2: yup
    .string()
    .default('')
  ,
  tobacco: yup.string(),
  birthDate: yup
    .string()
    .required('Birthdate is required'),
  zip: yup
    .string()
    .required('Zip is required'),
  countyName: yup.string().required('County is required'),
  state: yup.string(),
});

export interface ContactFormValues {
  email?: string | null;
  birthDate?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  phoneNumber2?: string | null;
  gender?: string | null;
  zip?: string | null;
  countyName?: string | null,
  state?: string | null,
  tobacco?: string | null,
  profileId?: string | null
}

interface ContactFormProps {
  client: ContactFormValues,
  onSubmit: (values?: ContactFormValues, data?: SaveUserProfileMutation['saveUserProfile']) => void
}

const ContactForm = forwardRef<HTMLFormElement, ContactFormProps>((props, ref) => {
  const {client} = props;
  const [checkEmail, {loading: checkEmailLoading}] = useCheckUserExistenceByEmailLazyQuery();

  const formik = useFormik({
    initialValues: {
      email: client.email || '',
      firstName: client.firstName || '',
      lastName: client.lastName || '',
      phoneNumber: client.phoneNumber ? formatToPhoneNumber(client.phoneNumber) : '',
      phoneNumber2: client.phoneNumber2 ? formatToPhoneNumber(client.phoneNumber2) : '',
      gender: client.gender || '',
      zip: client.zip || '',
      countyName: client.countyName || '',
      state: client.state || '',
      tobacco: typeof client.tobacco === 'string' ? client.tobacco : '',
      birthDate: client.birthDate ? moment(client.birthDate).format('MM/DD/YYYY') : '',
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    onSubmit: async values => {
      if (formik.isValid) {
        if (values.email && values.email !== client?.email) {
          const res = await checkEmail({ variables: { email: values.email } });
          if (res.data?.checkUserExistenceByEmail) {
            formik.setFieldError('email', 'This email is assigned to other client');
            return;
          }
        }
        const phoneNumber = values.phoneNumber.replace(/\s|\(|\)|-/gm, '');
        if (phoneNumber && !/^\+1\d{10}$/.test(phoneNumber)) {
          formik.setFieldError('phoneNumber', 'Wrong phone number format');
          return;
        }
        const phoneNumber2 = values.phoneNumber2.replace(/\s|\(|\)|-/gm, '');
        if (phoneNumber2 && !/^\+1\d{10}$/.test(phoneNumber2)) {
          formik.setFieldError('phoneNumber2', 'Wrong phone number format');
          return;
        }

        save({
          ...values,
          tobacco: values.tobacco ? values.tobacco === 'true' : undefined,
          birthDate: moment(values.birthDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
          phoneNumber,
          phoneNumber2,
          profileId: client?.profileId
        })
      }
    },
  });

  const { save, loading: saveLoading } = useSaveProfile(data => props.onSubmit(formik.values, data));

  const loading = checkEmailLoading || saveLoading;

  return (
    <Stack component={'form'} gap={2} ref={ref} onSubmit={formik.handleSubmit}>
      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormikTextInput formik={formik} name={'firstName'} required  />
        <FormikTextInput formik={formik} name={'lastName'} required />
      </Stack>

      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormikDateInput formik={formik} name={'birthDate'} required />
        <FormikTextInput formik={formik} name={'email'} required />
      </Stack>

      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <PhoneNumberInput value={formik.values.phoneNumber}
                          label={'Primary phone number *'}
                          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                          placeholder={'+1 (000) 000-0000'}
                          onChange={e => (
                            formik.setFieldValue('phoneNumber', e.target.value)
                          )} />
        <PhoneNumberInput value={formik.values.phoneNumber2}
                          label={'Secondary phone number'}
                          error={formik.touched.phoneNumber2 && Boolean(formik.errors.phoneNumber2)}
                          helperText={formik.touched.phoneNumber2 && formik.errors.phoneNumber2}
                          placeholder={'+1 (000) 000-0000'}
                          onChange={e => (
                            formik.setFieldValue('phoneNumber2', e.target.value)
                          )} />
      </Stack>

      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormikSelectInput formik={formik} name={'gender'} required>
          <MenuItem value={''}>-</MenuItem>
          <MenuItem value={'M'}>Male</MenuItem>
          <MenuItem value={'F'}>Female</MenuItem>
        </FormikSelectInput>
        <FormikSelectInput formik={formik} name={'tobacco'}>
          <MenuItem value={''}>-</MenuItem>
          <MenuItem value={'true'}>Yes</MenuItem>
          <MenuItem value={'false'}>No</MenuItem>
        </FormikSelectInput>
      </Stack>

      <ZipSelector onChange={(data) => {
        formik.setFieldValue('zip', data.zip);
        formik.setFieldValue('countyName', data.county);
        formik.setFieldValue('state', data.state);
      }}
                          inputProps={{
                            label: 'Zip code *',
                            error: formik.touched.zip && Boolean(formik.errors.zip) || formik.touched.countyName && Boolean(formik.errors.countyName),
                            helperText: formik.touched.zip && formik.errors.zip || formik.touched.countyName && formik.errors.countyName
                          }}
                          zip={formik.values.zip}
                          county={formik.values.countyName}
                          state={formik.values.state} />

      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Button onClick={() => props.onSubmit()} variant={'outlined'}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>

    </Stack>
  );
})



export const CreateProfileButton = (props: ButtonProps & {status?: StatusDto, followUpDate?: string, spouseButton?: boolean}) => {
  const client = useClient();
  const {status = StatusDto.Attempted, followUpDate, spouseButton, ...rest} = props;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createQuote] = useCreateMedicareQuoteForClientMutation({client: mainBackendApolloClient});
  const [saveStatus] = useSaveStatusMutation();
  const [createGroup] = useMergeClientsInOneGroupMutation();

  const onProfileCreated = async (values?: ContactFormValues, data?: SaveUserProfileMutation['saveUserProfile']) => {
    if (values && data) {
      await createQuote({
        variables: {
          clientId: data.clientId,
          data: {
            county: values.countyName,
            zip: values.zip,
            planYear: GetDefaultPlansYear(),
            filters: {
              extraBenefits: [MedicareQuoteFilterExtraBenefit.DrugCoverage],
            },
          }
        }
      })

      await saveStatus({
        variables: {
          agentId: keycloak?.tokenParsed?.sub,
          id: data.clientId,
          status,
          followUpDate,
        }
      })

      if (spouseButton) {
        await createGroup({
          variables: {
            clientIds: [client.id, data.clientId]
          }
        })
      }

      navigate('/' + getRoute('client') + '/' + data.clientId)
    }

    setOpen(false)
  }

  const initial = spouseButton ? ({
    ...client,
    tobacco: typeof client.tobacco === 'boolean' ? client.tobacco.toString() : '',
    gender: client.gender === 'M' ? 'F' : 'M',
    lastName: client.lastName + '-Spouse',
    email: '',
    profileId: undefined
  }) : { ...client, tobacco: typeof client.tobacco === 'boolean' ? client.tobacco.toString() : ''}

  return <>
    <Dialog fullWidth maxWidth={'sm'} open={open} onClose={() => setOpen(false)}>
      <DialogTitle variant={'h3'}>
        Contact info
        <ModalCloseButton onClick={() => setOpen(false)} sx={{position: 'absolute', top: 4, right: 8}} />
      </DialogTitle>
      <DialogContent>
        <ContactForm client={initial} onSubmit={onProfileCreated} />
      </DialogContent>
    </Dialog>
    <Button data-test={'create-new-client-button'} variant={'contained'} size={'small'} sx={{minWidth: 150, alignSelf: 'center'}} {...rest} onClick={e => {
      props.onClick && props.onClick(e)
      setOpen(true)
    }}>
      {rest.children || <>
        Create new client
      </>}
    </Button>
  </>
}

export const ContactFormEditButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const client = useClient();
  const [open, setOpen] = useState(false);

  return <>
    <Dialog fullWidth maxWidth={'sm'} open={open} onClose={() => setOpen(false)}>
      <DialogTitle variant={'h3'}>
        Contact info
        <ModalCloseButton onClick={() => setOpen(false)} sx={{position: 'absolute', top: 4, right: 8}} />
      </DialogTitle>
      <DialogContent>
        <ContactForm client={{ ...client, tobacco: typeof client.tobacco === 'boolean' ? client.tobacco.toString() : ''}} onSubmit={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
    <IconButton ref={ref} size={'small'} sx={{position: 'absolute', right: 0}} {...props} onClick={e => {
      props.onClick && props.onClick(e)
      setOpen(true)
    }}>
      <Pen color="#a6a6a6" />
    </IconButton>
  </>
})
